import { ShoppingCartOutlined } from '@ant-design/icons';
import {
  EnumOnereWithDettaglioDTOStato,
  EnumTipoOnereDTOTipoVincolo,
  getGetFrontRichiesteIdQueryKey,
  usePostFrontRichiesteIdPagamenti,
  type OnereWithDettaglioDTO,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { burdensColumns } from '@framework/frontoffice-submission';
import { useTranslation } from '@framework/i18n';
import {
  ButtonResponsive,
  CurrencyRender,
  TableData,
  VerticalSpace,
} from '@framework/ui';
import { ButtonProps, Col, message, Row, Typography } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import { sum } from 'lodash';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

const { Text } = Typography;

const StyledTotalValueContainer = styled.div`
  display: flex;
  justify-self: stretch;
  align-items: center;
  padding: 0 16px;
  gap: 6px;
  border: 2px solid ${props => props.theme.colorBorder};
  border-radius: ${props => props.theme.borderRadius};
  font-size: 18px;
  flex-grow: 1;
  width: 100%;
  .ant-typography {
    margin: 0;
    font-size: 16px;
  }
`;

interface BurdenSelectionTableProps {
  burdens: OnereWithDettaglioDTO[];
  readonly?: boolean;
  paymentButtonProps?: ButtonProps;
}

export function BurdenSelectionTable(props: BurdenSelectionTableProps) {
  const { burdens, readonly = false, paymentButtonProps } = props;

  const [selected, setSelected] = useState<OnereWithDettaglioDTO[]>([]);
  const totalValue = sum(selected.map(s => s.valore ?? 0));

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const isAllAfterSendBurdens = burdens.every(
    b =>
      b.tipoOnere?.tipoVincolo ===
      EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO,
  );

  const rowSelection: TableRowSelection<OnereWithDettaglioDTO> = {
    selectedRowKeys: selected.map(s => s.id!),
    onChange: (_, selected) => setSelected(selected),
    columnTitle: checkboxNode => {
      if (React.isValidElement(checkboxNode)) {
        return React.cloneElement(checkboxNode, {
          'aria-label': t(
            'burden-selection-table.seleziona-oneri',
            'Seleziona tutti gli oneri',
          ),
        } as any);
      }
      return checkboxNode;
    },
    getCheckboxProps: record => ({
      'aria-label':
        t('burden-selection-table.seleziona-onere', 'Seleziona onere ') +
        record.descrizione,
      disabled:
        record.stato === EnumOnereWithDettaglioDTOStato.PAGATO ||
        record.stato === EnumOnereWithDettaglioDTOStato.ATTESA_PAGAMENTO ||
        record.tipoOnere?.tipoVincolo ===
          EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO,
    }),
  };

  const { mutate: createPayment, isLoading } = usePostFrontRichiesteIdPagamenti(
    {
      mutation: {
        onSuccess: () => {
          const submissionId = selected[0].idRichiesta;
          setSelected([]);

          // NOTE: Should this throw an error?
          if (!submissionId) return;

          const submissionQueryKey =
            getGetFrontRichiesteIdQueryKey(submissionId)[0];
          queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              (queryKey[0] as string).includes(submissionQueryKey),
          });
        },
        onError: error => {
          message.error(getNetworkErrorMessage(error, t("burden-selection-table.errore-in-creazione-pagamento", 'Si è verificato un errore durante la creazione del pagamento. Riprovare.'))); // prettier-ignore
        },
      },
    },
  );

  const handleCreatePayment = () => {
    const submissionId = selected[0].idRichiesta;

    // NOTE: Should this throw an error?
    if (!submissionId) return;

    createPayment({
      id: submissionId,
      data: {
        richiestaOneri: selected.map(({ id }) => ({ id })),
      },
    });
  };

  return (
    <>
      <TableData
        size="small"
        pagination={false}
        columns={burdensColumns}
        rowSelection={
          readonly || isAllAfterSendBurdens ? undefined : rowSelection
        }
        dataSource={burdens}
      />
      <div>
        <VerticalSpace size={24} />
        {!isAllAfterSendBurdens && (!readonly || totalValue > 0) && (
          <Row gutter={12}>
            {(!readonly || totalValue > 0) && (
              <Col
                span={12}
                md={12}
                sm={16}
                xs={18}
                style={{ display: 'flex' }}
              >
                <StyledTotalValueContainer>
                  <Text>
                    {t('burden-table.totale-selezionato', 'Totale selezionato')}
                    :
                  </Text>
                  <Text strong>
                    <CurrencyRender value={totalValue} />
                  </Text>
                </StyledTotalValueContainer>
              </Col>
            )}

            {!readonly && (
              <Col span={12} md={12} sm={8} xs={6}>
                <ButtonResponsive
                  size="large"
                  block
                  iconLeftComponent={ShoppingCartOutlined}
                  iconLeftAriaLabel={t(
                    'burden-table.crea-pagamento',
                    'Crea Pagamento',
                  )}
                  text={t('burden-table.crea-pagamento', 'Crea Pagamento')}
                  loading={isLoading}
                  disabled={selected.length === 0}
                  onClick={handleCreatePayment}
                  {...paymentButtonProps}
                />
              </Col>
            )}
          </Row>
        )}
      </div>
    </>
  );
}
